/**
 * File Utilities
 *
 * Helpers for naming and triggering browser downloads of (signed) document URLs.
 */

/**
 * Strip characters that are illegal or problematic in filenames (and in the
 * Content-Disposition header used by Supabase signed download URLs).
 *
 * @param name - The raw filename base (e.g. a rapport- or offertenummer)
 * @returns A filename-safe string
 */
export function sanitizeFileName(name: string): string {
  return name
    .replace(/[\\/:*?"<>|#%&{}]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Maximum size for opdrachtgever/installateur-uploaded documents.
 * Matches the 10MB limit used for photo uploads in Constatering.vue.
 */
export const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Allow-list mapping a validated MIME type to the extension and Content-Type we
 * will actually store the file under.
 *
 * SECURITY: the storage path and the `contentType` passed to the public
 * `userFiles` bucket must NEVER be derived from the untrusted `file.name`
 * extension or the browser-reported `file.type` verbatim. A crafted `file.name`
 * can smuggle `../` path segments into the storage key, and a spoofed
 * `file.type` can set an attacker-chosen Content-Type on a public object
 * (enabling e.g. HTML/JS served inline from our origin). Everything the caller
 * uses for the path/contentType is resolved from this server-safe allow-list.
 */
const UPLOAD_KIND_BY_MIME: Record<string, { extension: string; contentType: string }> = {
  "application/pdf": { extension: "pdf", contentType: "application/pdf" },
  "image/jpeg": { extension: "jpg", contentType: "image/jpeg" },
  "image/png": { extension: "png", contentType: "image/png" },
  "image/gif": { extension: "gif", contentType: "image/gif" },
  "image/webp": { extension: "webp", contentType: "image/webp" },
};

export interface ValidatedUpload {
  /** Safe extension derived from the allow-list (never from file.name). */
  extension: string;
  /** Safe Content-Type derived from the allow-list (never from file.type). */
  contentType: string;
}

export type UploadValidationResult =
  | { ok: true; value: ValidatedUpload }
  | { ok: false; error: string };

/**
 * Validate a user-selected file before uploading it to the public userFiles
 * bucket: enforces a max size and an allow-list of MIME types, and returns a
 * SAFE extension + Content-Type to use for the storage path (so a crafted
 * filename/MIME can never influence the storage key or served Content-Type).
 *
 * @param file - The file the user selected
 * @param options.allowImages - When true, image types are accepted in addition
 *   to PDF (used where the flow legitimately accepts photos). Defaults to
 *   PDF-only.
 */
export function validateUploadedFile(
  file: File,
  options: { allowImages?: boolean } = {},
): UploadValidationResult {
  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    return { ok: false, error: "Bestand is te groot. Maximum grootte is 10MB." };
  }

  const allowedMimes = options.allowImages
    ? Object.keys(UPLOAD_KIND_BY_MIME)
    : ["application/pdf"];

  // Only fall back to the filename extension when the browser reports no type;
  // the kind is still resolved through the allow-list below.
  let mime = file.type;
  if (!mime && /\.pdf$/i.test(file.name)) {
    mime = "application/pdf";
  }

  const kind = allowedMimes.includes(mime) ? UPLOAD_KIND_BY_MIME[mime] : undefined;
  if (!kind) {
    return {
      ok: false,
      error: options.allowImages
        ? "Ongeldig bestandstype. Upload een PDF- of afbeeldingsbestand."
        : "Ongeldig bestandstype. Upload een PDF-bestand.",
    };
  }

  return { ok: true, value: { extension: kind.extension, contentType: kind.contentType } };
}

/**
 * Trigger a browser download of a URL under a specific filename via a
 * programmatic anchor click (avoids the blank-tab flash and popup blockers
 * that window.open causes).
 *
 * @param url - The (signed) URL to download
 * @param fileName - The filename the browser should save the file as
 */
export function triggerFileDownload(url: string, fileName: string): void {
  const link = window.document.createElement("a");
  link.href = url;
  link.download = fileName;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
}
